'use client';
import { Button, ButtonColor, ButtonSize } from '@leland-dev/leland-ui-library';
import { usePathname, useSearchParams } from 'next/navigation';
import { type FC, useCallback } from 'react';

import BlurredDesktopImage from '../../assets/images/srp-page/blurred-desktop.png';
import BlurredMobileImage from '../../assets/images/srp-page/blurred-mobile.png';
import TeamMembersImage from '../../assets/images/srp-page/team-members.png';
import { useAuth } from '../../context/AuthContext';
import mxpnl from '../../utils/analytics/mixpanel';
import phog from '../../utils/analytics/posthog/posthog';
import { TYPEFORM_URLS } from '../../utils/constants';
import {
  convertUrlSearchParamsToObject,
  getHashUrlString,
} from '../../utils/url';
import NextJSImage from '../image/NextJSImage';

interface LowAvailabilitySrpContentProps {
  baseTrackingVars: Optional<{
    categorySlug: string;
    subCategorySlug?: string;
  }>;
  subCategoryOrCategoryTitle: string;
}

export const LowAvailabilitySrpContent: FC<LowAvailabilitySrpContentProps> = ({
  baseTrackingVars,
  subCategoryOrCategoryTitle,
}) => {
  const { currentUser } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const href = getHashUrlString(TYPEFORM_URLS.TALK_TO_TEAM_MEMBER, {
    first_name: currentUser?.firstName,
    last_name: currentUser?.lastName,
    email: currentUser?.email,
    id: currentUser?.applicant?.id,
    referrer: pathname,
    ...convertUrlSearchParamsToObject(searchParams),
  });

  const onClickTalkToATeamMemberButton = useCallback(() => {
    mxpnl.track(
      'low-availability-srp - click - Talk to a Team Member button',
      baseTrackingVars,
    );
    phog.capture('srp--low_availability_srp_talk_to_team_member_click', {
      ...baseTrackingVars,
    });
  }, [baseTrackingVars]);

  return (
    <div className="relative flex size-full items-start justify-center">
      <NextJSImage
        className="hidden size-full object-cover object-left-top sm:block"
        src={BlurredDesktopImage}
        alt=""
      />
      <NextJSImage
        className="block size-full object-cover object-left-top sm:hidden"
        src={BlurredMobileImage}
        alt=""
      />
      <div className="absolute inset-0 flex w-full items-start justify-center pt-10 sm:pt-20">
        <div className="sticky top-40 flex w-11/12 flex-col items-center justify-center gap-6 rounded-lg border bg-leland-white px-3 py-4 shadow-md sm:top-64 sm:w-1/2 sm:px-6 sm:py-8">
          <NextJSImage
            src={TeamMembersImage}
            className="h-auto w-20"
            alt="team members image"
          />
          <h3 className="text-center text-2xl font-medium leading-7 text-leland-gray-dark">
            Get matched to a top-rated {subCategoryOrCategoryTitle}
            coach.
          </h3>
          <p className="text-left text-base font-normal leading-6 text-leland-gray-light sm:text-center">
            Set up a time with a Leland team member so we can get you matched
            with a coach that fits your budget, timeline, and goals.
          </p>
          <a href={href} target="_blank" rel="noreferrer">
            <Button
              label="Continue"
              size={ButtonSize.MEDIUM}
              buttonColor={ButtonColor.BLACK}
              rounded={true}
              onClick={onClickTalkToATeamMemberButton}
              id="low_availability_srp--talk_to_a_team_member_button"
            />
          </a>
        </div>
      </div>
    </div>
  );
};
